/**
 * The end-of-run reveal, as data (TASKS T57, SPEC §1, BRIEF <game>).
 *
 * > A run ends when the timer expires; the reward is the end-of-run reveal showing what was promoted,
 * > what returns tomorrow, and which conversion rules are now generating words for free.
 *
 * This is the game's only reward surface, and the constraint on it is severe: **nothing here may be
 * maximisable**. Every number on this card is a description of the schedule — how many items moved,
 * when they come back — and not a measure of how well the run went. There is no score, no total, no
 * "best", no comparison with the last run, and nothing that gets bigger by playing longer in a way
 * the learner could chase.
 *
 * That is why the counts are *lists of items*, not tallies. "Six promoted" is a number to beat;
 * "aquí, cobija, dormir…" is a statement about six particular words. The distinction is the whole
 * difference between an instrument and a scoreboard.
 *
 * Pure. No DOM, no clock reading — `now` is passed in.
 */

import type { ItemRecord } from '../store/schema';
import type { RunSummary } from './gamestate';

/** One item, as the card names it. */
export interface RevealLine {
  id: string;
  es: string;
  en: string;
  /** When it comes back, in words. Empty where the section does not report timing. */
  detail: string;
}

/**
 * A conversion rule the learner proved this run.
 *
 * `exceptions` is a **true** count — the hazards that rule carries in this corpus — and it is what
 * makes the generative claim honest. See DECISIONS D-152.
 */
export interface RevealRule {
  id: string;
  /** The rule as the learner met it: `-tion becomes -ción`. */
  rule: string;
  /** `Applies to every English -tion word, except 3 you have met here.` */
  reach: string;
}

export interface RevealView {
  promoted: RevealLine[];
  returning: RevealLine[];
  introduced: RevealLine[];
  rules: RevealRule[];
  /** Nothing happened this run. A real and unremarkable outcome, not a failure. */
  empty: boolean;
}

export interface RevealInput {
  summary: RunSummary;
  /** The banked records, by id. Whatever is missing is simply not listed. */
  items: ReadonlyMap<string, ItemRecord>;
  /** Rules proven this run, already resolved to what the card shows. */
  rules: readonly RevealRule[];
  /** From the injected clock. */
  now: number;
}

const DAY_MS = 86_400_000;

/**
 * When an item comes back, in words.
 *
 * Days, never hours or minutes: an interval measured in hours invites the learner to sit and wait
 * for it, which is the opposite of spacing. "Tomorrow" is the floor, and it is a fact rather than an
 * instruction — nothing on this card asks him to come back.
 */
export function returnLabel(dueAt: number, now: number): string {
  const days = Math.round((dueAt - now) / DAY_MS);
  if (days <= 0) return 'later today';
  if (days === 1) return 'tomorrow';
  return `in ${days} days`;
}

function lineFor(
  id: string,
  items: ReadonlyMap<string, ItemRecord>,
  detail: (record: ItemRecord) => string,
): RevealLine | null {
  const record = items.get(id);
  if (!record) return null;
  return { id, es: record.es, en: record.en, detail: detail(record) };
}

function lines(
  ids: readonly string[],
  items: ReadonlyMap<string, ItemRecord>,
  detail: (record: ItemRecord) => string,
): RevealLine[] {
  return ids
    .map((id) => lineFor(id, items, detail))
    .filter((line): line is RevealLine => line !== null);
}

export function revealView(input: RevealInput): RevealView {
  const { summary, items, now } = input;

  const promoted = lines(summary.promoted, items, (record) =>
    returnLabel(record.memory.dueAt, now),
  );
  const returning = lines(summary.returningTomorrow, items, (record) =>
    returnLabel(record.memory.dueAt, now),
  );
  // No timing on a new item: it has one, but "back tomorrow" beside a word met ninety seconds ago
  // is noise, and the fact worth reporting is that it exists now.
  const introduced = lines(summary.newItemsIntroduced, items, () => '');

  return {
    promoted,
    returning,
    introduced,
    rules: [...input.rules],
    empty:
      promoted.length === 0 &&
      returning.length === 0 &&
      introduced.length === 0 &&
      input.rules.length === 0,
  };
}

// ---------------------------------------------------------------------------------------------
// Which rules the learner proved
// ---------------------------------------------------------------------------------------------

/** The one attempt fact that counts as having proved a rule. */
export interface RuleAttempt {
  itemId: string;
  runId: string;
  correct: boolean;
  hinted: boolean;
}

/**
 * Rules whose **first** clean success falls in this run.
 *
 * "Unlocked" is measured, like everything else in this game: a rule is generating words for the
 * learner once he has produced one of its words correctly, unaided. Not when the tier opened, and
 * not when he first saw the rule — either of those would put the card's claim ahead of the evidence
 * for it.
 *
 * First-ever, so a rule is announced once and never again. A card that re-announced it every run
 * would be a card that congratulates, and the reveal does not do that.
 */
export function rulesProvenIn(
  runId: string,
  attempts: readonly RuleAttempt[],
  ruleOf: (itemId: string) => string | undefined,
): string[] {
  const firstRun = new Map<string, string>();
  for (const attempt of attempts) {
    if (!attempt.correct || attempt.hinted) continue;
    const rule = ruleOf(attempt.itemId);
    if (rule === undefined || firstRun.has(rule)) continue;
    firstRun.set(rule, attempt.runId);
  }
  return [...firstRun.entries()]
    .filter(([, first]) => first === runId)
    .map(([rule]) => rule)
    .sort();
}

/**
 * The generative claim, with a number that is actually true.
 *
 * The brief asks for "a count of words that rule now generates for free". There is no honest way to
 * count those — it would be a claim about the learner's English vocabulary that nothing here
 * measures — so the number reported is the one that *is* countable: the exceptions. "Applies to
 * every English -tion word, except 3 you have met here" says the generative thing and says it
 * truthfully. See DECISIONS D-152.
 */
export function ruleReach(from: string, exceptions: number): string {
  const scope = `Applies to every English ${from} word`;
  if (exceptions === 0) return `${scope}.`;
  return `${scope}, except ${exceptions === 1 ? '1 you have met here' : `${exceptions} you have met here`}.`;
}
